import React from "react";
import { Card, Typography, Avatar, Space } from "antd";
import {
    EllipsisOutlined,
    HeartOutlined,
    MessageOutlined,
    SendOutlined,
    SaveOutlined,
    LeftOutlined,
    RightOutlined
} from "@ant-design/icons";
import TextRenderer from "../../utils/TextUtils/textRendererForLivePreview";
import {useTranslation} from "react-i18next";


export default function InstagramPreview({ text, images = [], profileName }) {
    const [index, setIndex] = React.useState(0);
    const [expanded, setExpanded] = React.useState(false);
    const clamp = 440;
    const uname = (profileName || "account").toString();
    const initials = uname?.[0]?.toUpperCase() || "A";
    const {t} = useTranslation();

    const hasMultiple = images.length > 1;
    const current = images[index]?.url;

    const onPrev = (e) => {
        e.stopPropagation();
        setIndex((i) => (i - 1 + images.length) % images.length);
    };
    const onNext = (e) => {
        e.stopPropagation();
        setIndex((i) => (i + 1) % images.length);
    };

    return (
        <Card size="small" bordered>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <Space align="center">
                    <Avatar size={32} style={{ background: "#d9d9d9" }}>{initials}</Avatar>
                    <Typography.Text strong>{uname}</Typography.Text>
                </Space>
                <EllipsisOutlined style={{ fontSize: 18, color: "#595959" }} />
            </div>

            {/* Media (square) */}
            <div
                style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1/1",
                    background: current ? `#000 url(${current}) center/cover no-repeat` : "#f0f0f0",
                    borderRadius: 12,
                    overflow: "hidden"
                }}
            >
                {/* index counter */}
                {hasMultiple && (
                    <div style={{
                        position: "absolute", top: 8, right: 8,
                        background: "rgba(0,0,0,0.45)", color: "#fff",
                        borderRadius: 16, padding: "2px 8px", fontSize: 12, fontWeight: 600
                    }}>
                        {index + 1}/{images.length}
                    </div>
                )}

                {/* arrows */}
                {hasMultiple && (
                    <>
                        <button
                            type="button"
                            onClick={onPrev}
                            style={{
                                position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)",
                                width: 32, height: 32, borderRadius: "50%", border: "none",
                                background: "rgba(0,0,0,0.45)", color: "#fff", cursor: "pointer"
                            }}
                        >
                            <LeftOutlined />
                        </button>
                        <button
                            type="button"
                            onClick={onNext}
                            style={{
                                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                                width: 32, height: 32, borderRadius: "50%", border: "none",
                                background: "rgba(0,0,0,0.45)", color: "#fff", cursor: "pointer"
                            }}
                        >
                            <RightOutlined />
                        </button>
                    </>
                )}
            </div>

            {/* actions row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                <Space size={16}>
                    <HeartOutlined style={{ fontSize: 20 }} />
                    <MessageOutlined style={{ fontSize: 20 }} />
                    <SendOutlined style={{ fontSize: 20 }} />
                </Space>
                <SaveOutlined style={{ fontSize: 20 }} />
            </div>

            {/* dots */}
            {hasMultiple && (
                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 6 }}>
                    {images.map((_, i) => (
                        <span
                            key={i}
                            style={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: i === index ? "#262626" : "#bfbfbf", display: "inline-block"
                            }}
                        />
                    ))}
                </div>
            )}

            {/* caption */}
            {text && (
                <div style={{ marginTop: 4 }}>
                    <Typography.Paragraph style={{ marginBottom: 4, lineHeight: 1.25 }}>
                        {expanded ? (
                            <>
                                <TextRenderer text={text} />
                                <Typography.Link
                                    onClick={() => setExpanded(false)}
                                    style={{ display: "block", marginTop: 4, marginLeft: 0, padding: 0, lineHeight: "inherit" }}                                >
                                    {t("post.rollUp")}
                                </Typography.Link>
                            </>
                        ) : (
                            <>
                                <TextRenderer text={text} clampChars={clamp} />
                                {text.length > clamp && (
                                    <Typography.Link
                                        onClick={() => setExpanded(true)}
                                        style={{ display: "block", marginTop: 4, marginLeft: 0, padding: 0, lineHeight: "inherit" }}                                    >
                                        {t("post.showMore")}
                                    </Typography.Link>
                                )}
                            </>
                        )}
                    </Typography.Paragraph>
                </div>
            )}
        </Card>
    );
}
